<template>
  <div>
    <v-row
      class="no-print ma-0"
      align="center"
      justify="start"
    >
      <v-switch
        v-model="groupByGroup"
        :label="'Group by ' + groupLabel"
        dense
        hide-details
        class="mb-2"
      />
    </v-row>

    <member-selector
      class="no-print"
      :members="members"
      :groups="groups"
      :selected-member.sync="selectedMember"
      :selected-group.sync="selectedGroups"
      :selected-job-level.sync="selectedJobLevel"
    />
    <v-data-table
      :group-by="groupByGroup ? 'group' : ''"
      sort-by="total"
      sort-desc
      :headers="headers"
      :items-per-page="filteredData.length"
      dense
      item-key="memberId"
      :expanded="expanded"
      :items="filteredData"
      :loading="loading"
      show-expand
    >
      <template v-slot:group.header="{items, isOpen, toggle}">
        <th
          v-if="groupByGroup"
          :colspan="headers.length + 1"
        >
          <v-icon @click="toggle">
            {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
          </v-icon>
          <span style="font-size: large">{{ groupMap[items[0].group] || items[0].group || 'Others' }} — {{ items.length }} members, {{ groupTotal(items) }} leave days</span>
        </th>
      </template>
      <template v-slot:item.sn="{ index }">
        <span>{{ index + 1 }}</span>
      </template>
      <template
        v-for="(t, i) in effectiveLeaveTypes"
        v-slot:[`item.leave_${i}`]="{ item }"
      >
        <span
          v-if="!(item[`leave_${i}`])"
          :key="t"
          class="grey--text text--lighten-1"
        >-</span>
        <v-chip
          v-else
          :key="t"
          x-small
          :color="leaveColor(i)"
          text-color="white"
        >
          {{ item[`leave_${i}`] }}
        </v-chip>
      </template>
      <template v-slot:item.total="{ item }">
        <span
          v-if="!item.total"
          class="grey--text text--lighten-1"
        >-</span>
        <strong v-else>{{ item.total | number('0.0') }}</strong>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <div
            v-if="!hasAnyLeave(item)"
            class="pa-2 grey--text"
          >
            No approved leaves in this period.
          </div>
          <v-row
            v-else
            dense
            class="pa-2"
          >
            <v-col
              v-for="t in leaveTypesWithRecords(item)"
              :key="t"
              cols="12"
              sm="6"
              md="4"
            >
              <div class="detail-subheader">
                <v-icon
                  x-small
                  class="leave-icon mr-1"
                >
                  mdi-beach
                </v-icon>
                {{ t }} ({{ (item.byType && item.byType[t]) || 0 }})
              </div>
              <div class="simple-dates body-2">
                <span
                  v-for="(a, idx) in item.details[t]"
                  :key="idx"
                >
                  <span v-if="idx">, </span>{{ a.date | moment('DD MMM') }}<span v-if="+a.days === 0.5"> (0.5)</span>
                </span>
              </div>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import moment from 'moment'
  import { fetchLeaveReport } from '@/helpers/tikApi'
  import MemberSelector from '@/views/dashboard/components/MemberSelector'
  import { exportLeaveReportToExcel } from '@/helpers/exportLeaveReport'

  const FALLBACK_LEAVE_TYPES = ['Annual Leave', 'Annual Leave (No Rest)', 'Maternity Leave', 'Sick Leave', 'Special Permission', 'Paternity Leave', 'Unpaid Leave', 'Bereavement Leave', 'Other']

  const LEAVE_TYPE_COLORS = ['blue', 'teal', 'orange', 'purple', 'indigo', 'pink', 'cyan', 'green', 'red', 'amber darken-2', 'lime darken-2', 'blue-grey']

  export default {
    name: 'LeaveReport',
    components: { MemberSelector },
    data: () => ({
      expanded: [],
      groupByGroup: true,
      leaveTypes: [],
      rows: [],
      loading: false,
      selectedMember: [],
      selectedGroups: [],
      selectedJobLevel: [],
    }),
    computed: {
      ...mapState('records', ['view', 'currentDate', 'startDate', 'endDate']),
      ...mapGetters([
        'team', 'company', 'teamFull', 'groups', 'user', 'roles', 'workCloseDays', 'userGroups', 'groupsFull', 'groupLabel', 'companySettings',
      ]),
      groupMap () {
        return (this.groupsFull || []).reduce((acc, g) => {
          acc[g.code] = g.name
          return acc
        }, {})
      },
      effectiveLeaveTypes () {
        if (this.leaveTypes && this.leaveTypes.length) return this.leaveTypes
        const configured = this.companySettings && this.companySettings.lists && this.companySettings.lists.leaveTypes
        const raw = (configured && configured.length ? configured : FALLBACK_LEAVE_TYPES)
        // Merge "Annual Leave (No Rest)" into "Annual Leave" (backend does the same)
        const seen = new Set()
        const out = []
        raw.forEach(label => {
          const key = String(label || '').replace(/\s/g, '').toLowerCase()
          const norm = (key === 'annualleave(norest)' || key === 'annualleavenorest') ? 'annualleave' : key
          if (!norm || seen.has(norm)) return
          seen.add(norm)
          out.push(norm === 'annualleave' ? 'Annual Leave' : String(label))
        })
        if (!seen.has('other')) out.push('Other')
        return out
      },
      tableRows () {
        return (this.rows || []).map(r => {
          const flat = { ...r }
          this.effectiveLeaveTypes.forEach((t, i) => {
            flat[`leave_${i}`] = (r.byType && r.byType[t]) || 0
          })
          return flat
        })
      },
      filteredData () {
        return this.tableRows.filter(m =>
          (!this.selectedMember.length || this.selectedMember.includes(m.memberId)) &&
          (!this.selectedGroups.length || this.selectedGroups.includes(m.group)) &&
          (!this.selectedJobLevel.length || (m.jobLevel && this.selectedJobLevel.includes(m.jobLevel))),
        )
      },
      headers () {
        const cols = [
          { text: '#', value: 'sn', width: '40px', align: 'center', sortable: false },
          { text: 'Name', value: 'name', width: '160px' },
        ]
        this.effectiveLeaveTypes.forEach((t, i) => {
          cols.push({ text: t, value: `leave_${i}`, align: 'center', width: '90px' })
        })
        cols.push({ text: 'Total', value: 'total', align: 'center', width: '80px' })
        return cols
      },
      members () {
        if (!this.rows) return []
        return this.rows.map(r => ({
          id: r.memberId,
          fullName: r.name,
          jobTitle: r.jobTitle,
        }))
      },
    },
    watch: {
      startDate () {
        this.loadReport()
      },
      endDate () {
        this.loadReport()
      },
      team () {
        this.loadReport()
      },
      selectedGroups () {
        this.loadReport()
      },
      groups (newGroups) {
        if (newGroups && newGroups.length > 0 && this.selectedGroups.length === 0) {
          this.selectedGroups = [newGroups[0]]
        }
      },
    },
    created () {
      this.loadReport()
    },
    mounted () {
      this.$root.$on('export-leavereport-excel', this.exportToExcel)
    },
    beforeDestroy () {
      this.$root.$off('export-leavereport-excel', this.exportToExcel)
    },
    methods: {
      async loadReport () {
        if (!this.startDate || !this.endDate || !this.team) return
        this.loading = true
        try {
          const groups = this.selectedGroups.length ? this.selectedGroups.join(',') : null
          const data = await fetchLeaveReport(
            this.startDate,
            this.endDate,
            this.team,
            false,
            groups,
          )
          if (Array.isArray(data)) {
            this.rows = data
            this.leaveTypes = []
          } else {
            this.rows = data.rows || []
            this.leaveTypes = data.leaveTypes || []
          }
        } catch (e) {
          console.error('Failed to load leave report', e)
        } finally {
          this.loading = false
        }
      },
      hasAnyLeave (item) {
        return (item.total || 0) > 0
      },
      leaveTypesWithRecords (item) {
        return this.effectiveLeaveTypes.filter(t => item.details && item.details[t] && item.details[t].length)
      },
      groupTotal (items) {
        return items.reduce((sum, i) => sum + (+i.total || 0), 0).toFixed(1)
      },
      leaveColor (index) {
        return LEAVE_TYPE_COLORS[index % LEAVE_TYPE_COLORS.length]
      },
      exportToExcel () {
        const sDate = moment(this.startDate).format('DD/MM/YYYY')
        const eDate = moment(this.endDate).format('DD/MM/YYYY')
        const companyHeader = `${this.company || ''} ${this.teamFull?.name || ''}`.trim()

        exportLeaveReportToExcel({
          items: this.filteredData,
          leaveTypes: this.effectiveLeaveTypes,
          groupBy: this.groupByGroup,
          companyHeader,
          title: 'Leave Report',
          dateRangeText: `Period: ${sDate} - ${eDate}`,
          groupLabel: this.groupLabel,
          groupMap: this.groupMap,
          filename: `Leave Report - ${sDate.replace(/\//g, '-')}`,
        })
      },
    },
  }
</script>

<style scoped>
.leave-icon {
  color: #42a5f5 !important;
}
.detail-subheader {
  font-weight: bold;
  font-size: 12px;
  margin: 8px 0 4px 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.simple-dates {
  line-height: 1.6;
}
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
